// A tiny router on the History API: the server sends index.html for every route outside /api.

export interface SearchRoute {
  name: 'search';
  q: string;
  /** Conversation, author and period filters (`7`, `30`, `year`). */
  conversation: string | null;
  author: string | null;
  period: string | null;
}

export type Route =
  | { name: 'login' }
  | { name: 'activate'; token: string }
  | { name: 'home' }
  | { name: 'saved' }
  | { name: 'security' }
  | { name: 'profile' }
  | { name: 'admin' }
  | SearchRoute
  /** `thread`: a thread open on the right; `focus`: a message to show in its context. */
  | { name: 'conversation'; id: string; thread: string | null; focus: string | null }
  /** The files tab, with the file shown in the preview. */
  | { name: 'files'; id: string; file: string | null }
  /** Phase 2: the board of a project, and « Mes tâches ». */
  | { name: 'board'; id: string }
  | { name: 'tasks' };

function parse(path: string, search: string): Route {
  const parts = path.split('/').filter(Boolean).map(decodeURIComponent);
  const params = new URLSearchParams(search);
  if (parts[0] === 'login') return { name: 'login' };
  if (parts[0] === 'saved') return { name: 'saved' };
  if (parts[0] === 'tasks') return { name: 'tasks' };
  if (parts[0] === 'settings' && parts[1] === 'security') return { name: 'security' };
  if (parts[0] === 'settings') return { name: 'profile' };
  if (parts[0] === 'admin') return { name: 'admin' };
  if (parts[0] === 'search') {
    return {
      name: 'search',
      q: params.get('q') ?? '',
      conversation: params.get('c'),
      author: params.get('a'),
      period: params.get('p'),
    };
  }
  if (parts[0] === 'activate' && parts[1]) return { name: 'activate', token: parts[1] };
  if (parts[0] === 'c' && parts[1]) {
    if (parts[2] === 'files') return { name: 'files', id: parts[1], file: parts[3] ?? null };
    if (parts[2] === 'tasks') return { name: 'board', id: parts[1] };
    return {
      name: 'conversation',
      id: parts[1],
      thread: parts[2] === 't' && parts[3] ? parts[3] : null,
      focus: params.get('m'),
    };
  }
  return { name: 'home' };
}

class Router {
  route = $state<Route>(parse(location.pathname, location.search));

  constructor() {
    addEventListener('popstate', () => {
      this.route = parse(location.pathname, location.search);
    });
  }

  go(target: string, options: { replace?: boolean } = {}): void {
    const url = new URL(target, location.origin);
    if (url.pathname + url.search === location.pathname + location.search) return;
    if (options.replace) history.replaceState(null, '', target);
    else history.pushState(null, '', target);
    this.route = parse(url.pathname, url.search);
  }
}

export const router = new Router();

export const conversationPath = (id: string) => `/c/${encodeURIComponent(id)}`;
export const threadPath = (id: string, root: string) => `${conversationPath(id)}/t/${encodeURIComponent(root)}`;
/** The conversation, opened around this message (saved, pinned, search). A reply opens its
 * thread too. */
export const messagePath = (id: string, message: string, root: string | null = null) =>
  `${root ? threadPath(id, root) : conversationPath(id)}?m=${encodeURIComponent(message)}`;
export const boardPath = (id: string) => `${conversationPath(id)}/tasks`;
export const filesPath = (id: string, file: string | null = null) =>
  `${conversationPath(id)}/files${file ? `/${encodeURIComponent(file)}` : ''}`;

export function searchPath(params: { q: string; conversation?: string | null; author?: string | null; period?: string | null }): string {
  const query = new URLSearchParams();
  if (params.q) query.set('q', params.q);
  if (params.conversation) query.set('c', params.conversation);
  if (params.author) query.set('a', params.author);
  if (params.period) query.set('p', params.period);
  const text = query.toString();
  return text ? `/search?${text}` : '/search';
}
